import type { Hsl, Rgb } from './color.interface';

const HEX_COLOR_PATTERN = /^#[0-9A-F]{6}$/;

/** WCAG 2.x contrast ratio between two `#RRGGBB` colors, from 1 to 21. */
export function contrastRatio(first: string, second: string): number {
  const a = relativeLuminance(first);
  const b = relativeLuminance(second);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** WCAG 2.x relative luminance of a `#RRGGBB` color, from 0 to 1. */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return (
    0.2126 * linearChannel(r) +
    0.7152 * linearChannel(g) +
    0.0722 * linearChannel(b)
  );
}

export function isHexColor(value: string): boolean {
  return HEX_COLOR_PATTERN.test(value.toUpperCase());
}

export function hexToRgb(hex: string): Rgb {
  const value = parseInt(hex.slice(1), 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const toHex = (channel: number) =>
    Math.round(channel).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

export function hexToHsl(hex: string): Hsl {
  const { r, g, b } = hexToRgb(hex);
  const [red, green, blue] = [r / 255, g / 255, b / 255];
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const l = (max + min) / 2;
  const delta = max - min;
  if (delta === 0) return { h: 0, s: 0, l };

  const s = delta / (1 - Math.abs(2 * l - 1));
  return { h: hueDegrees([red, green, blue], max, delta), s, l };
}

export function hslToHex({ h, s, l }: Hsl): string {
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const [red, green, blue] = hueSectorChannels(h, chroma, x);
  const m = l - chroma / 2;
  return rgbToHex({
    r: (red + m) * 255,
    g: (green + m) * 255,
    b: (blue + m) * 255,
  });
}

/** `color` painted at `ratio` opacity over white, mixed in sRGB like CSS. */
export function mixOverWhite(color: string, ratio: number): string {
  const { r, g, b } = hexToRgb(color);
  const mix = (channel: number) => 255 + (channel - 255) * ratio;
  return rgbToHex({ r: mix(r), g: mix(g), b: mix(b) });
}

function linearChannel(channel: number): number {
  const value = channel / 255;
  return value <= 0.04045
    ? value / 12.92
    : Math.pow((value + 0.055) / 1.055, 2.4);
}

function hueDegrees(
  [red, green, blue]: [number, number, number],
  max: number,
  delta: number,
): number {
  if (max === red) return ((((green - blue) / delta) % 6) * 60 + 360) % 360;
  if (max === green) return ((blue - red) / delta + 2) * 60;
  return ((red - green) / delta + 4) * 60;
}

function hueSectorChannels(
  h: number,
  chroma: number,
  x: number,
): [number, number, number] {
  const sectors: [number, number, number][] = [
    [chroma, x, 0],
    [x, chroma, 0],
    [0, chroma, x],
    [0, x, chroma],
    [x, 0, chroma],
    [chroma, 0, x],
  ];
  return sectors[Math.floor(h / 60) % 6];
}
